"use client";

import { useMemo, type ReactNode } from "react";
import { clientApi } from "@frontend/lib/api";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { useAsync } from "@frontend/lib/use-async";
import {
  WorkspaceSideProvider,
  type WorkspaceSide,
} from "@frontend/lib/workspace-side";
import type { Viewer } from "@creators/backend/lib/auth";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";

/**
 * Loads the inbox once (shared cache with TopNav) and decides whether the
 * signed-in account can flip between creator and collaborator.
 */
export function WorkspaceSideGate({
  viewer,
  children,
}: {
  viewer: Viewer;
  children: ReactNode;
}) {
  const inbox = useAsync(
    async () => {
      if (viewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs");
      return collabs;
    },
    [viewer.sessionId, viewer.isAdmin, viewer.creator?.id],
    { cacheKey: collabsCacheKey(viewer.sessionId) },
  );

  const collabs = inbox.data ?? [];

  const { canToggle, forcedSide } = useMemo(() => {
    const hasCreatorCollab = collabs.some((c) => c.yourRole === "creator");
    const hasBrandCollab = collabs.some((c) => c.yourRole === "brand");
    const hasCreatorProfile = Boolean(viewer.creator);
    const hasBrandProfile = Boolean(viewer.brand);

    const creatorPossible = hasCreatorProfile || hasCreatorCollab;
    const brandPossible = hasBrandProfile || hasBrandCollab;
    const canToggle = creatorPossible && brandPossible;

    let forcedSide: WorkspaceSide | null = null;
    if (!canToggle) {
      forcedSide = brandPossible && !creatorPossible ? "brand" : "creator";
    }

    return { canToggle, forcedSide };
  }, [collabs, viewer.creator, viewer.brand]);

  return (
    <WorkspaceSideProvider canToggle={canToggle} forcedSide={forcedSide}>
      {children}
    </WorkspaceSideProvider>
  );
}
